<script setup>
import {computed, ref, watch} from 'vue';
import {useI18n} from 'vue-i18n';
import {createConfirmDialog} from 'vuejs-confirm-dialog';
import LineChart from '@/components/echarts/LineChart.vue';
import CurrencyInput from '@/components/forms/CurrencyInput.vue';
import BSTooltip from '@/components/tooltips/BSTooltip.vue';
import CalculationStrategyModal from '@/components/modals/CalculationStrategyModal.vue';
import OnboardingBondsSection from '@/components/bonds/OnboardingBondsSection.vue';
import {formatAmount, formatNumber, formatYearsString} from '@/helper';
import {
  STRATEGY_COLORS,
  allocationPct,
  applyTargetMode,
  chartData,
  normalizeStrategies,
  strategyResult,
  syncLegacyFields,
} from '@/utils/onboardingCalculation';

/**
 * Krok Kalkulačka v ASO/SO: více investičních strategií jako karty (přidání a úprava v modalu, odebrání),
 * graf s linkou za každou strategii, režim investičního plánu / cíle a pod strategiemi sekce Dluhopisy.
 *
 * Data se upravují přímo v step.data (stejně jako dřív v ClientOnboardingOverlayNew), odesílá je rodič.
 * Výpočty jsou v utils/onboardingCalculation.js.
 */
const props = defineProps({
  // krok Kalkulačka: data, available_values (strategie z dotazníku), bonds (nabídka dluhopisů)
  step: {type: Object, required: true},
  // limity částek podle měny {CZK: {initial_deposit_min, ...}, EUR: {...}}
  currencySettingsByCurrency: {type: Object, default: () => ({})},
  // výchozí limity, když pro měnu chybí vlastní
  defaultSettings: {type: Object, default: () => ({})},
  // odkaz „více informací“ o strategiích (in-equity / biginvest)
  portfoliaUrl: {type: String, default: null},
  // problémy dluhopisů z BE a zvýrazněný dluhopis (návrat ze Shrnutí)
  bondProblems: {type: Array, default: () => []},
  highlightBondId: {type: Number, default: null},
});
// validity – lze pokračovat (strategie i dluhopisy v pořádku)
const emit = defineEmits(['validity']);
// sekce Dluhopisy – tlačítko „Přidat dluhopis“ v záhlaví otevírá její výběr dluhopisů
const bondsSection = ref(null);

const {t} = useI18n();
const data = computed(() => props.step.data);
const availableStrategies = computed(() => Array.isArray(props.step.available_values) ? props.step.available_values : []);
const strategyById = id => availableStrategies.value.find(s => s.id === Number(id)) ?? null;
const currency = computed(() => data.value.parameters?.general?.currency ?? 'CZK');
const currencySettings = computed(() => props.currencySettingsByCurrency?.[currency.value] ?? props.defaultSettings ?? {});
const isTarget = computed(() => data.value.mode === 'TARGET');
// cílová částka v EUR má nižší minimum (dosavadní pravidlo kalkulačky)
const targetMin = computed(() => currency.value === 'EUR' ? 5000 : 100000);

// převod starého tvaru (jedna strategie) a výchozí karta pro novou kalkulačku; znovu i když rodič
// po uložení kroku dosadí nová data z BE (jiný objekt kroku)
watch(() => props.step, () => normalizeStrategies(data.value, availableStrategies.value), {immediate: true});

/** Strategie, které lze ještě přidat (každá jen jednou). */
const addableStrategies = computed(() => availableStrategies.value.filter(s => !data.value.strategies.some(item => item.strategy_id === s.id)));

/**
 * Přepočet při změně vstupů: v režimu cíle dopočet investic a vždy údaje první strategie pro BE.
 * Sleduje se jen „podpis“ vstupů – dopočtené částky by jinak watcher spouštěly znovu.
 */
watch(() => JSON.stringify([
  data.value.mode,
  data.value.parameters?.target?.amount,
  data.value.parameters?.general?.duration,
  data.value.goal_split,
  data.value.strategies.map(item => [item.strategy_id, item.alloc_pct]),
]), () => {
  applyTargetMode(data.value, strategyById);
  syncLegacyFields(data.value, strategyById);
}, {immediate: true});

/** Výsledky karet (vloženo, očekávaná hodnota, zhodnocení) pro zobrazení i součty. */
const results = computed(() => data.value.strategies.map(item => strategyResult(item, strategyById(item.strategy_id))));
const totalInvested = computed(() => results.value.reduce((sum, r) => sum + r.invested, 0));
const totalExpected = computed(() => results.value.reduce((sum, r) => sum + r.expected, 0));
const chart = computed(() => chartData(data.value, strategyById, {
  deposits: t('clientOnboardingOverlay.deposited_funds'),
  inflation: t('clientOnboardingOverlay.expected_inflation_rate'),
}));

/** Součet vlastního rozdělení cílové částky – musí dát 100 %. */
const splitTotal = computed(() => data.value.strategies.reduce((sum, item) => sum + (Number(item.alloc_pct) || 0), 0));
const splitInvalid = computed(() => isTarget.value && data.value.goal_split === 'CUSTOM' && data.value.strategies.length > 1 && Math.abs(splitTotal.value - 100) > 0.5);

// dluhopisy v kalkulačce jen tam, kde je BE nabízí (enabled: false = B.I.G. Invest SK/CZ, sekce se vůbec nezobrazí)
const bondsEnabled = computed(() => props.step.bonds?.enabled !== false);
// onboarding může obsahovat jen strategie, jen dluhopisy nebo obojí – alespoň jedno z toho musí být vybrané
const hasBonds = computed(() => bondsEnabled.value && Array.isArray(data.value.bonds) && data.value.bonds.length > 0);
const nothingSelected = computed(() => !data.value.strategies.length && !hasBonds.value);
const strategiesValid = computed(() => !nothingSelected.value && !splitInvalid.value);
let bondsValid = true;
watch(strategiesValid, value => emit('validity', value && bondsValid), {immediate: true});
function onBondsValidity(value) {
  bondsValid = value;
  emit('validity', strategiesValid.value && bondsValid);
}

/** Společná částka pro všechny strategie (zaškrtávátka v režimu plánu). */
function applySharedAmounts() {
  const saving = data.value.parameters.saving;
  for (const item of data.value.strategies) {
    if (data.value.same_initial_deposit) {
      item.initial_deposit = Number(saving.initial_deposit) || 0;
    }
    if (data.value.same_regular_deposit) {
      item.regular_deposit = Number(saving.regular_deposit) || 0;
    }
  }
}

/** Změna měny: částky se srovnají do limitů nové měny (např. 5 mil. Kč je nad maximem v EUR). */
function onCurrencyChange() {
  const limits = currencySettings.value;
  const clamp = (value, min, max) => Math.min(Math.max(Number(value) || 0, min ?? 0), max ?? Number.MAX_SAFE_INTEGER);
  for (const item of data.value.strategies) {
    item.initial_deposit = clamp(item.initial_deposit, limits.initial_deposit_min, limits.initial_deposit_max);
    item.regular_deposit = clamp(item.regular_deposit, limits.regular_deposit_min, limits.regular_deposit_max);
  }
  const saving = data.value.parameters.saving;
  saving.initial_deposit = clamp(saving.initial_deposit, limits.initial_deposit_min, limits.initial_deposit_max);
  saving.regular_deposit = clamp(saving.regular_deposit, limits.regular_deposit_min, limits.regular_deposit_max);
}

/** Text o alokaci a dopočtené investici do modalu v režimu cíle. */
function targetInfo(item) {
  if (!item) {
    return t('onboardingCalculation.target_info_new');
  }
  return t('onboardingCalculation.target_info', {
    pct: formatNumber(allocationPct(data.value, item), 0),
    amount: formatAmount(item.regular_deposit, currency.value, 0),
    years: data.value.parameters.general.duration,
  });
}

/** Přidání (index null) nebo úprava strategie v modalu. */
function openStrategyModal(index = null) {
  const item = index === null ? null : data.value.strategies[index];
  const choices = item ? [strategyById(item.strategy_id), ...addableStrategies.value].filter(Boolean) : addableStrategies.value;
  const saving = data.value.parameters.saving;
  const {reveal, onConfirm} = createConfirmDialog(CalculationStrategyModal, {
    item: item ? {...item} : null,
    strategies: choices,
    mode: data.value.mode,
    currency: currency.value,
    limits: currencySettings.value,
    // nová strategie začíná s nulovými vklady (částky zadá poradce); jen se zaškrtnutou společnou částkou
    // pro všechny strategie se předvyplní společná částka, aby karta odpovídala ostatním
    defaults: {
      initial_deposit: data.value.same_initial_deposit ? Number(saving.initial_deposit) || 0 : 0,
      regular_deposit: data.value.same_regular_deposit ? Number(saving.regular_deposit) || 0 : 0,
      duration: Number(data.value.parameters.general.duration) || 30,
    },
    targetInfo: isTarget.value ? targetInfo(item) : null,
  });
  reveal();
  onConfirm(saved => {
    if (index === null) {
      data.value.strategies.push(saved);
      // nová strategie v rovnoměrném rozdělení – vlastní rozdělení doplní uživatel
      if (data.value.goal_split === 'CUSTOM') {
        saved.alloc_pct = 0;
      }
    } else {
      data.value.strategies.splice(index, 1, {...data.value.strategies[index], ...saved});
    }
    if (!isTarget.value) {
      applySharedAmounts();
    }
  });
}

function removeStrategy(index) {
  data.value.strategies.splice(index, 1);
}

const strategyColor = index => STRATEGY_COLORS[index % STRATEGY_COLORS.length];
const ratePct = rate => formatNumber(rate * 100, 1) + ' % p.a.';
</script>

<template>
  <div class="onboarding-calculation">
    <!-- přidání strategie a dluhopisu vedle sebe; název strategie se zadává v modalu Přidat / Upravit strategii
         (pole „název portfolia“ kalkulačka nemá) -->
    <div class="row mb-3 align-items-end">
      <div class="col-12 col-md-6 form-group mb-2">
        <label class="form-label">
          {{ t('clientOnboardingOverlay.strategy') }}
          <a v-if="portfoliaUrl" :href="portfoliaUrl" target="_blank" :title="t('clientOnboardingOverlay.calculation_more_info_website')"><span class="mdi mdi-information-outline ms-1"></span></a>
        </label>
        <button type="button" class="btn btn-outline-tenant btn-lg w-100 calculation-header-control d-flex align-items-center justify-content-center gap-1" :disabled="!addableStrategies.length" @click="openStrategyModal()">
          <span class="mdi mdi-plus calculation-header-control__icon"></span><span>{{ t('onboardingCalculation.add_strategy') }}</span>
        </button>
      </div>
      <div v-if="bondsEnabled" class="col-12 col-md-6 form-group mb-2">
        <label class="form-label">{{ t('onboardingBonds.title') }}</label>
        <button type="button" class="btn btn-outline-tenant btn-lg w-100 calculation-header-control d-flex align-items-center justify-content-center gap-1" :disabled="!bondsSection?.canAddMore" @click="bondsSection?.openSelect()">
          <span class="mdi mdi-plus calculation-header-control__icon"></span><span>{{ t('onboardingCalculation.add_bond') }}</span>
        </button>
      </div>
      <div class="col-12 col-md-6">
        <small v-if="!addableStrategies.length && data.strategies.length" class="text-muted">{{ t('onboardingCalculation.all_strategies_added') }}</small>
      </div>
      <div v-if="bondsEnabled" class="col-12 col-md-6">
        <!-- klientovi nejde nabídnout žádný další dluhopis (dotazník, cílový trh, nic v prodeji) -->
        <small v-if="!bondsSection?.canAddMore" class="text-muted">{{ t('onboardingBonds.no_offer') }}</small>
      </div>
    </div>

    <!-- graf: linka za každou strategii, vložené prostředky a inflace -->
    <div class="card mb-4">
      <div class="card-body">
        <!-- LineChart výšku sám nenastavuje – bez pevné výšky obalu má graf nulovou výšku -->
        <div v-if="data.strategies.length" class="calculation-chart">
          <LineChart :digits="0" :series="chart.series" :valuesType="'amount'" :categoriesType="'years'"
                     :categories="chart.categories" :currency="currency"/>
        </div>
        <div v-else class="text-center text-muted py-5">{{ t('onboardingCalculation.chart_empty') }}</div>
      </div>
    </div>

    <!-- režim: investiční cíl / plán -->
    <div class="btn-group w-100 mb-4" role="group" :aria-label="t('onboardingCalculation.mode')">
      <input v-model="data.mode" type="radio" class="btn-check" name="calculationMode" id="calculationModeTarget" autocomplete="off" value="TARGET">
      <label class="btn btn-outline-primary" for="calculationModeTarget">{{ t('clientOnboardingOverlay.calculation_your_investment_goal') }}
        <BSTooltip :title="t('clientOnboardingOverlay.calculation_your_investment_goal_tooltip')"><i class="mdi mdi-information-outline ms-2"/></BSTooltip>
      </label>
      <input v-model="data.mode" type="radio" class="btn-check" name="calculationMode" id="calculationModeSaving" autocomplete="off" value="SAVING">
      <label class="btn btn-outline-primary" for="calculationModeSaving">{{ t('clientOnboardingOverlay.calculation_your_investment_plan') }}
        <BSTooltip :title="t('clientOnboardingOverlay.calculation_your_investment_plan_tooltip')"><i class="mdi mdi-information-outline ms-2"/></BSTooltip>
      </label>
    </div>

    <div class="d-flex align-items-center gap-3 mb-4">
      <span class="form-label mb-0">{{ t('clientOnboardingOverlay.currency') }}</span>
      <div class="form-check form-check-inline mb-0">
        <input v-model="data.parameters.general.currency" @change="onCurrencyChange" type="radio" class="form-check-input" name="calculationCurrency" id="calculationCurrencyCzk" value="CZK">
        <label class="form-check-label" for="calculationCurrencyCzk">CZK</label>
      </div>
      <div class="form-check form-check-inline mb-0">
        <input v-model="data.parameters.general.currency" @change="onCurrencyChange" type="radio" class="form-check-input" name="calculationCurrency" id="calculationCurrencyEur" value="EUR">
        <label class="form-check-label" for="calculationCurrencyEur">EUR</label>
      </div>
    </div>

    <!-- plán: stejné částky pro všechny strategie -->
    <div v-if="!isTarget" class="card mb-4">
      <div class="card-body">
        <div class="row align-items-center mb-2">
          <div class="col-12 col-md-8 form-check ps-5">
            <input v-model="data.same_initial_deposit" @change="applySharedAmounts" class="form-check-input" type="checkbox" id="sameInitialDeposit">
            <label class="form-check-label" for="sameInitialDeposit">{{ t('onboardingCalculation.same_initial_deposit') }}</label>
          </div>
          <div v-if="data.same_initial_deposit" class="col-12 col-md-4">
            <CurrencyInput v-model="data.parameters.saving.initial_deposit" :currency="currency" :min="currencySettings?.initial_deposit_min" :max="currencySettings?.initial_deposit_max" @blur="applySharedAmounts"/>
          </div>
        </div>
        <div class="row align-items-center">
          <div class="col-12 col-md-8 form-check ps-5">
            <input v-model="data.same_regular_deposit" @change="applySharedAmounts" class="form-check-input" type="checkbox" id="sameRegularDeposit">
            <label class="form-check-label" for="sameRegularDeposit">{{ t('onboardingCalculation.same_regular_deposit') }}</label>
          </div>
          <div v-if="data.same_regular_deposit" class="col-12 col-md-4">
            <CurrencyInput v-model="data.parameters.saving.regular_deposit" :currency="currency" :min="currencySettings?.regular_deposit_min" :max="currencySettings?.regular_deposit_max" @blur="applySharedAmounts"/>
          </div>
        </div>
      </div>
    </div>

    <!-- cíl: cílová částka, horizont a rozdělení mezi strategie -->
    <div v-else class="mb-4">
      <div class="row align-items-center mb-3">
        <label class="col-12 col-md-6 form-label mb-md-0">{{ t('clientOnboardingOverlay.calculation_how_much_you_want_saved') }}</label>
        <div class="col-12 col-md-6">
          <CurrencyInput v-model="data.parameters.target.amount" :currency="currency" :min="targetMin" :max="300000000"/>
        </div>
      </div>
      <div class="row align-items-center mb-3">
        <label class="col-12 col-md-6 form-label mb-md-0">{{ t('clientOnboardingOverlay.calculation_how_long_you_want_invest') }}</label>
        <div class="col-12 col-md-6">
          <vue-slider v-model="data.parameters.general.duration" :interval="1" :min="1" :max="50" :hide-label="true" :marks="false"/>
          <div class="vue-slider-result">
            <span class="vue-slider-result-label">{{ t('clientOnboardingOverlay.calculation_investment_horizon_is') }}:</span>
            <span class="vue-slider-result-value">{{ formatYearsString(data.parameters.general.duration) }}</span>
          </div>
        </div>
      </div>
      <div v-if="data.strategies.length > 1">
        <div class="form-label">{{ t('onboardingCalculation.split') }}</div>
        <div class="btn-group w-100 mb-2" role="group">
          <input v-model="data.goal_split" type="radio" class="btn-check" name="goalSplit" id="goalSplitEven" value="EVEN">
          <label class="btn btn-outline-primary" for="goalSplitEven">{{ t('onboardingCalculation.split_even') }}</label>
          <input v-model="data.goal_split" type="radio" class="btn-check" name="goalSplit" id="goalSplitCustom" value="CUSTOM">
          <label class="btn btn-outline-primary" for="goalSplitCustom">{{ t('onboardingCalculation.split_custom') }}</label>
        </div>
        <div v-if="data.goal_split === 'CUSTOM'" class="card">
          <div class="card-body">
            <div v-for="(item, index) of data.strategies" :key="item.strategy_id" class="d-flex justify-content-between align-items-center py-1">
              <span><span class="calculation-dot me-2" :style="{background: strategyColor(index)}"></span>{{ item.name }}</span>
              <span class="d-flex align-items-center gap-1">
                <input v-model.number="item.alloc_pct" type="number" min="0" max="100" step="1" class="form-control form-control-sm text-end" style="width: 80px;">%
              </span>
            </div>
            <div class="text-end small mt-1" :class="splitInvalid ? 'text-danger fw-bold' : 'text-muted'">{{ t('onboardingCalculation.split_total', {total: formatNumber(splitTotal, 0)}) }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- karty strategií -->
    <div class="row g-3 mb-4">
      <div v-for="(item, index) of data.strategies" :key="item.strategy_id" class="col-12 col-md-6">
        <div class="card h-100 calculation-strategy-card">
          <div class="card-header d-flex align-items-center gap-2">
            <span class="calculation-dot" :style="{background: strategyColor(index)}"></span>
            <span class="card-header__title flex-fill text-truncate">{{ item.name }}</span>
            <button type="button" class="btn btn-link p-1" :title="t('onboardingCalculation.edit_strategy')" @click="openStrategyModal(index)"><i class="mdi mdi-pencil"/></button>
            <button type="button" class="btn btn-link p-1" :title="t('onboardingCalculation.remove_strategy')" @click="removeStrategy(index)"><i class="mdi mdi-close"/></button>
          </div>
          <div class="card-body info-table">
            <template v-if="isTarget">
              <div class="info-table__row row">
                <span class="info-table__label col-7">{{ t('onboardingCalculation.allocation') }}</span>
                <span class="info-table__value col-5 text-end">{{ formatNumber(allocationPct(data, item), 0) }} %</span>
              </div>
              <div class="info-table__row row">
                <span class="info-table__label col-7">{{ t('onboardingCalculation.target_part') }}</span>
                <span class="info-table__value col-5 text-end">{{ formatAmount((Number(data.parameters.target.amount) || 0) * allocationPct(data, item) / 100, currency, 0) }}</span>
              </div>
              <div class="info-table__row row">
                <span class="info-table__label col-7">{{ t('clientOnboardingOverlay.calculation_recommended_monthly_investment') }}</span>
                <span class="info-table__value col-5 text-end">{{ formatAmount(item.regular_deposit, currency, 0) }} {{ t('onboardingCalculation.per_month') }}</span>
              </div>
            </template>
            <template v-else>
              <div class="info-table__row row">
                <span class="info-table__label col-7">{{ t('onboardingCalculation.initial_deposit') }}</span>
                <span class="info-table__value col-5 text-end">{{ formatAmount(item.initial_deposit, currency, 0) }}</span>
              </div>
              <div class="info-table__row row">
                <span class="info-table__label col-7">{{ t('onboardingCalculation.regular_deposit') }}</span>
                <span class="info-table__value col-5 text-end">{{ formatAmount(item.regular_deposit, currency, 0) }} {{ t('onboardingCalculation.per_month') }}</span>
              </div>
            </template>
            <div class="info-table__row row">
              <span class="info-table__label col-7">{{ t('onboardingCalculation.horizon') }}</span>
              <span class="info-table__value col-5 text-end">{{ formatYearsString(item.duration) }}</span>
            </div>
          </div>
          <!-- očekávané zhodnocení: hodnoty pod sebou v barvě aplikace, pata stejně vysoká ve všech kartách -->
          <div class="card-footer calculation-strategy-card__footer">
            <span class="fw-bold">{{ t('clientOnboardingOverlay.calculation_expected_return') }}</span>
            <span class="calculation-strategy-card__values">
              <span>{{ ratePct(results[index].rate) }}</span>
              <span>+{{ formatAmount(results[index].gain, currency, 0) }}</span>
            </span>
          </div>
        </div>
      </div>
      <div v-if="!data.strategies.length" class="col-12">
        <div class="calculation-empty text-center text-muted p-4">{{ bondsEnabled ? t('onboardingCalculation.no_strategy') : t('onboardingCalculation.no_strategy_without_bonds') }}</div>
      </div>
      <!-- bez strategií i dluhopisů nelze pokračovat -->
      <div v-if="nothingSelected" class="col-12">
        <div class="alert alert-warning mb-0">{{ bondsEnabled ? t('onboardingCalculation.no_strategy_or_bond') : t('onboardingCalculation.no_strategy_required') }}</div>
      </div>
    </div>

    <!-- součty strategií (onboarding jen s dluhopisy je nemá) -->
    <div v-if="data.strategies.length" class="row g-3 mb-4">
      <div class="col-12 col-md-6">
        <div class="card card--big-value card--ns h-100">
          <div class="card-body">
            <div class="card__value__label">{{ t('onboardingCalculation.total_invested') }}</div>
            <div class="card__value">{{ formatAmount(totalInvested, currency, 0) }}</div>
          </div>
        </div>
      </div>
      <div class="col-12 col-md-6">
        <div class="card card--big-value card--ns h-100">
          <div class="card-body">
            <div class="card__value__label">{{ t('onboardingCalculation.total_expected') }}</div>
            <div class="card__value">{{ formatAmount(totalExpected, currency, 0) }}</div>
          </div>
        </div>
      </div>
    </div>

    <hr v-if="bondsEnabled" class="my-4">

    <!-- dluhopisy pod strategiemi, vždy viditelné (kromě instance bez dluhopisů) -->
    <OnboardingBondsSection v-if="bondsEnabled" ref="bondsSection" v-model="data.bonds" :offer="step.bonds" :problems="bondProblems"
                            :highlight-bond-id="highlightBondId" @validity="onBondsValidity"/>
  </div>
</template>

<style scoped>
/*
  Přepínač Investiční cíl / plán: globální .btn-outline-primary má text v primaryTextColor (bílá, pro tmavé pozadí),
  takže nevybraná volba na bílém pozadí nebyla čitelná. Původní kalkulačka to řešila lokálním stylem v onboardingu,
  který se na tuto komponentu nevztahuje. Vybraná volba zůstává podle globálního stylu (bílý text na barvě aplikace).
*/
.btn-check:not(:checked) + .btn-outline-primary {
  color: var(--primaryColor);
}

/* název portfolia a „Přidat strategii“ vedle sebe ve stejné výšce (input form-control-lg je s paddingem projektu vyšší než btn-lg) */
.calculation-header-control {
  height: 50px;
  padding-top: 0;
  padding-bottom: 0;
}

/* ikona + je větší než text (.btn span.mdi 1.4em) – bez line-height 1 by seděla výš než text */
.calculation-header-control__icon {
  line-height: 1;
}

.calculation-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex: none;
}

/* pata karty strategie: popisek vlevo, hodnoty pod sebou vpravo; pevná výška, aby byly paty karet v řádku stejné */
.calculation-strategy-card__footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  min-height: 4.5rem;
}

.calculation-strategy-card__values {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  font-weight: 700;
  color: var(--primaryColor);
}

.calculation-chart {
  height: 320px;
}

.calculation-empty {
  border: 1.5px dashed #ced4da;
  border-radius: .375rem;
}
</style>
